// Контекст авторизации: пользователь, активная компания, роль, права.
import {
  createContext, useContext, useState, useEffect, ReactNode, useCallback,
} from 'react';
import { authApi, tokenStore } from '@/api';
import type { User, CompanyRole, RoleCode } from '@/types';
import { roleCan } from '@/lib/permissions';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  currentCompany: CompanyRole | null;
  login: (email: string, password: string, otp?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchCompany: (companyId: number) => void;
  can: (resource: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentCompanyId, setCurrentCompanyId] = useState<number | null>(
    tokenStore.getCompanyId() ? Number(tokenStore.getCompanyId()) : null
  );

  const loadUser = useCallback(async () => {
    if (!tokenStore.getAccess()) { setLoading(false); return; }
    try {
      const me = await authApi.me();
      setUser(me);
      if (!tokenStore.getCompanyId() && me.companies.length > 0) {
        tokenStore.setCompanyId(me.companies[0].company_id);
        setCurrentCompanyId(me.companies[0].company_id);
      }
    } catch {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = useCallback(async (email: string, password: string, otp?: string) => {
    const resp = await authApi.login(email, password, otp);
    setUser(resp.user);
    if (resp.user.companies.length > 0) setCurrentCompanyId(resp.user.companies[0].company_id);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setCurrentCompanyId(null);
  }, []);

  const switchCompany = useCallback((companyId: number) => {
    tokenStore.setCompanyId(companyId);
    setCurrentCompanyId(companyId);
    window.location.reload();
  }, []);

  const currentCompany =
    user?.companies.find((c) => c.company_id === currentCompanyId) ||
    user?.companies[0] || null;

  const can = useCallback(
    (resource: string, action: string) => {
      if (user?.is_platform_admin) return true;
      const role: RoleCode | undefined = currentCompany?.role;
      if (!role) return false;
      return roleCan(role, resource, action);
    },
    [user, currentCompany]
  );

  return (
    <AuthContext.Provider value={{
      user, loading, currentCompany, login, logout,
      refreshUser: loadUser, switchCompany, can,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth должен использоваться внутри AuthProvider');
  return ctx;
}
